'use server';
import { randomUUID } from 'node:crypto';

import { actionErrorLog } from '@/shared/infrastructure/logging/actionError';
import type { ActionResult } from '@/shared/types/actions';
import { SessionService } from '@/contexts/authentication';
import type { VoiceGender } from '@/contexts/ai/types/voice';

export interface VoiceInviteView {
  token: string;
  label: string;
  language: string;
  expiresAt: string;
}

/**
 * Mint a link that asks somebody for their voice.
 *
 * Nothing is charged here: the hold is taken when a recording actually arrives
 * (SHARED.MONEY — a link nobody used must not sit on the family's pool). The
 * voice-library entry id is minted with the link, so "waiting for the
 * recording" can be shown before anything exists.
 */
export async function createVoiceInvite(input: {
  label: string;
  language: string;
  gender?: VoiceGender;
}): Promise<ActionResult<VoiceInviteView>> {
  try {
    const userId = await SessionService.getUserId();
    const familyId = await SessionService.getFamilyId();
    if (!userId || !familyId) return { success: false, error: 'Not authenticated' };
    const label = (input.label ?? '').trim();
    if (label.length < 2) return { success: false, error: 'Name the voice first' };

    const { mintVoiceInvite } = await import('@/contexts/ai/services/voiceInvite.server');
    const invite = await mintVoiceInvite({
      familyId,
      createdBy: userId,
      label,
      language: input.language || 'ru',
      gender: input.gender,
      voiceEntryId: randomUUID(),
    });
    if ('error' in invite) return { success: false, error: 'too_many' };
    return {
      success: true,
      data: {
        token: invite.token,
        label: invite.label,
        language: invite.language,
        expiresAt: invite.expiresAt.toISOString(),
      },
    };
  } catch (e: unknown) {
    actionErrorLog('createVoiceInvite', e);
    return { success: false, error: e instanceof Error ? e.message : 'Failed to create the invite' };
  }
}

/** The invites this family is still waiting on. */
export async function listVoiceInvites(): Promise<ActionResult<VoiceInviteView[]>> {
  try {
    const familyId = await SessionService.getFamilyId();
    if (!familyId) return { success: false, error: 'Not authenticated' };
    const { pendingVoiceInvites } = await import('@/contexts/ai/services/voiceInvite.server');
    const rows = await pendingVoiceInvites(familyId);
    return {
      success: true,
      data: rows.map((i) => ({
        token: i.token,
        label: i.label,
        language: i.language,
        expiresAt: i.expiresAt.toISOString(),
      })),
    };
  } catch (e: unknown) {
    actionErrorLog('listVoiceInvites', e);
    return { success: false, error: 'Failed to read the invites' };
  }
}

/** Turn a link off. The row stays, so the token can never be handed out again. */
export async function cancelVoiceInvite(token: string): Promise<ActionResult<null>> {
  try {
    const familyId = await SessionService.getFamilyId();
    if (!familyId) return { success: false, error: 'Not authenticated' };
    const { revokeVoiceInvite } = await import('@/contexts/ai/services/voiceInvite.server');
    const ok = await revokeVoiceInvite(token, familyId);
    return ok ? { success: true, data: null } : { success: false, error: 'Not found' };
  } catch (e: unknown) {
    actionErrorLog('cancelVoiceInvite', e);
    return { success: false, error: 'Failed to cancel the invite' };
  }
}
